import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building, Plus, Trash2, Loader2, Download, Upload, Copy, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { LocalStorageManager } from "@/lib/storage";
import type { Project } from "@shared/schema";
import { projectExportSchema } from "@shared/schema";

export function ProjectList() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const importAbortRef = useRef<AbortController | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<Project | null>(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicateAddress, setDuplicateAddress] = useState("");

  useEffect(() => {
    return () => { importAbortRef.current?.abort(); };
  }, []);

  const { data: allProjects, isLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  const projects = useMemo(
    () => (allProjects ?? []).filter((p) => p.status !== "completed"),
    [allProjects],
  );

  const completedProjects = useMemo(
    () => (allProjects ?? []).filter((p) => p.status === "completed"),
    [allProjects],
  );

  const [showCompleted, setShowCompleted] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/projects/${id}`),
    onSuccess: (_data, id) => {
      LocalStorageManager.deleteProject(String(id));
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({
        title: "Projekt gelöscht",
        description: "Das Projekt wurde erfolgreich entfernt.",
      });
      setDeleteTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler beim Löschen",
        description: error.message || "Das Projekt konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: ({ id, name, address }: { id: number; name: string; address: string }) =>
      apiRequest("POST", `/api/projects/${id}/duplicate`, { name, address })
        .then((res) => res.json() as Promise<Project>),
    onSuccess: (newProject) => {
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({ title: "Projekt als Kopie angelegt.", description: `„${newProject.name}" wurde erstellt.` });
      setDuplicateTarget(null);
      navigate(`/project/edit/${newProject.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Duplizieren fehlgeschlagen",
        description: error.message || "Das Projekt konnte nicht dupliziert werden.",
        variant: "destructive",
      });
    },
  });

  const openDuplicateDialog = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setDuplicateName(`Kopie – ${project.name}`);
    setDuplicateAddress("");
    setDuplicateTarget(project);
  };

  const handleDuplicateSubmit = () => {
    if (!duplicateTarget || !duplicateName.trim() || !duplicateAddress.trim()) return;
    duplicateMutation.mutate({ id: duplicateTarget.id, name: duplicateName.trim(), address: duplicateAddress.trim() });
  };

  const handleExport = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setExportingId(project.id);
    try {
      const res = await fetch(`/api/projects/${project.id}/export`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || "Export fehlgeschlagen");
      }
      const json = await res.json();
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const date = new Date().toISOString().slice(0, 10);
      const safeName = project.name.replace(/[^a-zA-Z0-9äöüÄÖÜß_\-]/g, "_").slice(0, 50);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fassadencheck_${safeName}_${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Export fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setExportingId(null);
    }
  };

  const handleImportFile = async (file: File) => {
    if (!file.name.endsWith(".json")) {
      toast({ title: "Ungültige Datei", description: "Bitte eine .json-Exportdatei auswählen.", variant: "destructive" });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "Datei zu groß", description: "Die Exportdatei darf maximal 50 MB groß sein.", variant: "destructive" });
      return;
    }
    setImporting(true);
    toast({ title: "Projekt wird importiert …", description: "Bitte warten." });
    const abortCtrl = new AbortController();
    importAbortRef.current = abortCtrl;
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error("Die Datei enthält kein gültiges JSON.");
      }
      const validated = projectExportSchema.safeParse(parsed);
      if (!validated.success) {
        const firstIssue = validated.error.issues[0];
        if (firstIssue?.path?.includes("schemaVersion")) {
          throw new Error("Ungültiges Exportformat. Bitte eine gültige FassadenCheck-Exportdatei verwenden.");
        }
        throw new Error(`Ungültige Exportdatei: ${firstIssue?.message ?? "Unbekanntes Format"}`);
      }
      const res = await apiRequest("POST", "/api/projects/import", validated.data, abortCtrl.signal);
      const body = await res.json() as { projectId?: number };
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({
        title: "Projekt importiert",
        description: `„${validated.data.project.name}" wurde erfolgreich importiert.`,
      });
      if (body.projectId) {
        navigate(`/project/edit/${body.projectId}`);
      }
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return;
      toast({
        title: "Import fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
      importAbortRef.current = null;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-bauwerk-teal">Aktuelle Projekte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex justify-between items-center p-4 border rounded-lg">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-bauwerk-teal">Aktuelle Projekte</CardTitle>
          <p className="text-gray-600">Ihre letzten Erfassungen und deren Status</p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-white" />
            </div>
            <p className="text-gray-600 mb-4">Noch keine Projekte vorhanden</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate("/project/create")}
                className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
              >
                <Plus className="w-4 h-4 mr-2" />
                Erstes Projekt erstellen
              </Button>
              <Button
                variant="outline"
                disabled={importing}
                onClick={() => importInputRef.current?.click()}
                className="border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Projekt importieren
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-bauwerk-mint text-bauwerk-teal";
      case "completed":
        return "bg-green-100 text-green-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "In Bearbeitung";
      case "completed":
        return "Abgeschlossen";
      case "draft":
        return "Entwurf";
      default:
        return status;
    }
  };

  const isDeletingTarget = (id: number) =>
    deleteMutation.isPending && deleteTarget?.id === id;

  return (
    <>
      <input
        ref={importInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImportFile(file);
          e.target.value = "";
        }}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-bauwerk-teal">Aktuelle Projekte</CardTitle>
            <p className="text-gray-600 text-sm mt-1">Ihre letzten Erfassungen und deren Status</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={importing}
            onClick={() => importInputRef.current?.click()}
            className="border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Importieren
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projects.map((project) => (
              <div
                key={project.id}
                className="flex justify-between items-center p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/project/edit/${project.id}`)}
                data-testid={`project-row-${project.id}`}
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-bauwerk-teal mb-1">
                    {project.name}
                  </h4>
                  <p className="text-sm text-gray-600">{project.address}</p>
                  <p className="text-xs text-gray-500">
                    {project.createdAt ? new Date(project.createdAt).toLocaleDateString('de-DE') : ''}
                  </p>
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    → Klicken zum Bearbeiten (Schritt {project.currentStep} von 4)
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={getStatusColor(project.status)}>
                    {getStatusText(project.status)}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-gray-500 hover:text-bauwerk-teal hover:bg-bauwerk-mint"
                    aria-label={`Projekt ${project.name} duplizieren`}
                    onClick={(e) => openDuplicateDialog(project, e)}
                    data-testid={`button-duplicate-project-${project.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-gray-500 hover:text-bauwerk-teal hover:bg-bauwerk-mint"
                    aria-label={`Projekt ${project.name} exportieren`}
                    disabled={exportingId === project.id}
                    onClick={(e) => handleExport(project, e)}
                    data-testid={`button-export-project-${project.id}`}
                  >
                    {exportingId === project.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                    aria-label={`Projekt ${project.name} löschen`}
                    disabled={isDeletingTarget(project.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(project);
                    }}
                    data-testid={`button-delete-project-${project.id}`}
                  >
                    {isDeletingTarget(project.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {completedProjects.length > 0 && (
        <Card className="mt-4">
          <button
            type="button"
            className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 rounded-t-lg transition-colors"
            onClick={() => setShowCompleted((v) => !v)}
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-gray-700">
                Abgeschlossene Projekte ({completedProjects.length})
              </span>
            </div>
            {showCompleted ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
          {showCompleted && (
            <CardContent className="pt-0">
              <div className="space-y-3">
                {completedProjects.map((project) => (
                  <div
                    key={project.id}
                    className="flex justify-between items-center p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer bg-green-50/40"
                    onClick={() => navigate(`/project/edit/${project.id}`)}
                  >
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-700 mb-1">{project.name}</h4>
                      <p className="text-sm text-gray-600">{project.address}</p>
                      <p className="text-xs text-gray-500">
                        {project.createdAt ? new Date(project.createdAt).toLocaleDateString("de-DE") : ""}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-green-100 text-green-800">Abgeschlossen</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-gray-500 hover:text-bauwerk-teal hover:bg-bauwerk-mint"
                        aria-label={`Projekt ${project.name} als Vorlage kopieren`}
                        onClick={(e) => openDuplicateDialog(project, e)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-gray-500 hover:text-bauwerk-teal hover:bg-bauwerk-mint"
                        aria-label={`Projekt ${project.name} exportieren`}
                        disabled={exportingId === project.id}
                        onClick={(e) => handleExport(project, e)}
                      >
                        {exportingId === project.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                        aria-label={`Projekt ${project.name} löschen`}
                        disabled={isDeletingTarget(project.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(project);
                        }}
                      >
                        {isDeletingTarget(project.id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projekt löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Projekt{" "}
              <span className="font-semibold">{deleteTarget?.name}</span> wirklich
              unwiderruflich löschen? Alle zugehörigen Fotos werden mit entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id);
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
              data-testid="button-confirm-delete-project"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                "Löschen"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!duplicateTarget}
        onOpenChange={(open) => {
          if (!open && !duplicateMutation.isPending) setDuplicateTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-bauwerk-teal">
              <Copy className="w-5 h-5" />
              Projekt duplizieren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Das LV und alle Angebotseinstellungen werden übernommen. Fotos und
              Angebotsnummer starten neu.
            </p>
            <div className="space-y-2">
              <Label htmlFor="dup-name">Neuer Projektname</Label>
              <Input
                id="dup-name"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Projektname"
                disabled={duplicateMutation.isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dup-address">
                Neue Adresse / Bauvorhaben <span className="text-red-500">*</span>
              </Label>
              <Input
                id="dup-address"
                value={duplicateAddress}
                onChange={(e) => setDuplicateAddress(e.target.value)}
                placeholder="z. B. Musterstraße 12, 9871 Seeboden"
                disabled={duplicateMutation.isPending}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleDuplicateSubmit();
                }}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDuplicateTarget(null)}
              disabled={duplicateMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDuplicateSubmit}
              disabled={
                duplicateMutation.isPending ||
                !duplicateName.trim() ||
                !duplicateAddress.trim()
              }
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
              data-testid="button-confirm-duplicate-project"
            >
              {duplicateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird angelegt …
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Als Kopie anlegen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
